import { Decimal } from 'decimal.js';
import { computed, makeAutoObservable, observable } from "mobx";
import apiService from "../services/api/api.service";
import {ChartBar, MarketHistoryItem, MarketHistoryTimeframe} from "../services/api/api.interfaces";
import {IMergedMarketData, marketsStore} from "./MarketsStore";
import {TickerData} from "../services/socket/socket.service";
import {ResolutionString, SubscribeBarsCallback} from "../charting_library";


const timeframeToMilliseconds = {
    [MarketHistoryTimeframe["1M"]]: 1000 * 60,
    [MarketHistoryTimeframe["5M"]]: 1000 * 60 * 5,
    [MarketHistoryTimeframe["15M"]]: 1000 * 60 * 15,
    [MarketHistoryTimeframe["30M"]]: 1000 * 60 * 30,
    [MarketHistoryTimeframe["1H"]]: 1000 * 60 * 60,
    [MarketHistoryTimeframe["4H"]]: 1000 * 60 * 60 * 4,
    [MarketHistoryTimeframe["1D"]]: 1000 * 60 * 60 * 24,
}
class ChartStore {
    chartBars: Record<string, ChartBar[]> = {};
    chartBarsTimeframe: MarketHistoryTimeframe | undefined;
    dailyBars: Record<string, MarketHistoryItem[]> = {};

    subscriptionsByUuid = new Map<string, {
        ticker: string,
        callback: SubscribeBarsCallback
    }>()

    constructor() {
        makeAutoObservable(this, {
            chartBars: observable,
            chartBarsTimeframe: observable,
            dailyBars: observable,
            latestPrice: computed,
            dailyStats: computed
        });
    }

    get dailyStats() {
        const ticker = marketsStore.currentMarket?.ticker
        const bars = ticker && this.dailyBars[ticker];
        if (!bars || !bars.length || this.latestPrice <= 0) {
            return null;
        }

        const lastDailyBar =  this.dailyBars[ticker][bars.length - 1]
        const change = (this.latestPrice - lastDailyBar.open) / lastDailyBar.open * 100;
        const low = lastDailyBar.low;
        const high = lastDailyBar.high;

        return {
            change,
            low,
            high
        };
    }

    get latestPrice() {
        return Number(marketsStore.currentMarket?.lastPrice) || 0;
    }

    get currentMarketChartBars() {
        if (marketsStore.currentMarket) {
            return this.chartBars[marketsStore.currentMarket.ticker] || []
        }

        return []
    }

    async fetchBars(baseAsset: string, quoteAsset: string, timeframe: MarketHistoryTimeframe) {
        try {
            const response = await apiService.marketsHistoryGet({
                quoteAsset,
                baseAsset,
                timeframe
            });

            this.setChartBars(response, timeframe)
            this.setDailyBars(baseAsset, quoteAsset, timeframe, response)

            return this.currentMarketChartBars
        } catch (error) {
            console.error("Failed to fetch data:", error);
        }
        return []
    }
    async fetchDailyBars(baseAsset: string, quoteAsset: string) {
        try {
            const response = await apiService.marketsHistoryGet({
                quoteAsset,
                baseAsset,
                timeframe: MarketHistoryTimeframe['1D'],
                points: 2
            });

            this.setDailyBars(baseAsset, quoteAsset, MarketHistoryTimeframe['1D'], response)
        } catch (error) {
            console.error("Failed to fetch data:", error);
        }
        return []
    }

    setChartBars(historyItems: MarketHistoryItem[], timeframe: MarketHistoryTimeframe) {
        if (marketsStore.currentMarket?.ticker) {
            this.chartBars[marketsStore.currentMarket.ticker] = historyItems.map(item => ({
                time: item.date_time * 1000,
                low: item.low,
                high: item.high,
                open: item.open,
                close: item.close,
            }))
            this.chartBarsTimeframe = timeframe
        }
    }

    setDailyBars(baseAsset: string, quoteAsset: string,timeframe: MarketHistoryTimeframe, response: MarketHistoryItem[]) {
        if (timeframe === MarketHistoryTimeframe["1D"]) {
            const collectionCopy = { ...this.dailyBars };
            const key = `${baseAsset.toUpperCase()}${quoteAsset.toUpperCase()}`;
            collectionCopy[key] = response;
            this.dailyBars = collectionCopy;
        }
    }

    updateLatestPrice(tickerData: TickerData) {
        if (!tickerData) {
            return;
        }
        const { t: timestampMs, a: ask, b: bid, s: ticker } = tickerData;
        const currentMarketTicker = marketsStore.currentMarket?.ticker

        if (currentMarketTicker !== ticker) {
            return;
        }
        const price = new Decimal(ask).plus(bid).div(2).toNumber();

        const lastBar = this.applyLatestPriceOnChartBars(timestampMs, price);
        this.applyLatestPriceOnDailyBars(timestampMs, price);

        if (lastBar) {
            for (const subscriberUID of this.subscriptionsByUuid.keys()) {
                let subscription = this.subscriptionsByUuid.get(subscriberUID)

                if (subscription && subscription.ticker === ticker) {
                    subscription.callback({...lastBar})
                }
            }
        }
    }

    applyLatestPriceOnChartBars(timestampMs: number, latestPrice: number) {
        const chartBars = this.currentMarketChartBars
        if (!chartBars?.length || !this.chartBarsTimeframe) {
            return
        }
        let lastBar = chartBars[chartBars.length - 1];
        let lastBarTimeMs = lastBar.time

        let barSize = timeframeToMilliseconds[this.chartBarsTimeframe];

        let nextBarOpenTimeMs
        if (timestampMs - lastBarTimeMs > barSize) {
            nextBarOpenTimeMs = Math.round(timestampMs / barSize) * barSize

            let barOpenPrice = lastBar.close
            if (timestampMs - lastBarTimeMs > barSize * 2) {
                barOpenPrice = latestPrice
            }

            const nextBar = {
                time: nextBarOpenTimeMs,
                open: barOpenPrice,
                close: latestPrice,
                low: Math.min(latestPrice, latestPrice),
                high: Math.max(latestPrice, latestPrice)
            }

            chartBars.push(nextBar);
            lastBar = nextBar;
        } else {
            lastBar.close = latestPrice;
            lastBar.low = Math.min(lastBar.low, latestPrice);
            lastBar.high = Math.max(lastBar.high, latestPrice);
        }

        return lastBar;
    }

    applyLatestPriceOnDailyBars(timestampMs: number, latestPrice: number) {

        const currentMarketTicker = marketsStore.currentMarket?.ticker || ''
        const dailyBars = this.dailyBars[currentMarketTicker]

        if (!dailyBars?.length) {
            return
        }

        let lastBar = dailyBars[dailyBars.length - 1];
        let lastBarTimeMs = lastBar.date_time * 1000
        let barSize = timeframeToMilliseconds[MarketHistoryTimeframe["1D"]];

        let nextBarOpenTimeMs
        if (timestampMs - lastBarTimeMs > barSize) {
            nextBarOpenTimeMs = Math.round(timestampMs / barSize) * barSize

            let barOpenPrice = lastBar.close
            if (timestampMs - lastBarTimeMs > barSize * 2) {
                barOpenPrice = latestPrice
            }

            const nextBar: MarketHistoryItem = {
                date_time: nextBarOpenTimeMs / 1000,
                open: barOpenPrice,
                close: latestPrice,
                low: Math.min(latestPrice, latestPrice),
                high: Math.max(latestPrice, latestPrice)
            }

            dailyBars.push(nextBar);
        } else {
            lastBar.close = latestPrice;
            lastBar.low = Math.min(lastBar.low, latestPrice);
            lastBar.high = Math.max(lastBar.high, latestPrice);
        }
    }

    registerSubscription(param: { symbolItem: IMergedMarketData; resolution: ResolutionString; subscriberUID: string, subscribeBarsCallback: SubscribeBarsCallback }) {
        this.subscriptionsByUuid.set(param.subscriberUID, {
            ticker: param.symbolItem.ticker,
            callback: param.subscribeBarsCallback
        })
    }

    unregisterSubscription(subscriberUID: string) {
        this.subscriptionsByUuid.delete(subscriberUID)
    }
}

export const chartStore = new ChartStore();
